import { CanvasTexture, LinearFilter, Sprite, SpriteMaterial } from 'three';
import { getGlobeCityLabel } from './constants';

type SpriteEntry = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sprite: any;
  canvas: HTMLCanvasElement;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  texture: any;
  lastActive: boolean;
  lastText: string;
};

const cache = new Map<string, SpriteEntry>();

/** Screen-space scale when sizeAttenuation is off (readable at default globe zoom). */
const LABEL_SCREEN_SCALE = { default: 0.52, active: 0.68 } as const;

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
  ctx.lineTo(x + radius, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

function paintLabel(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  text: string,
  isActive: boolean,
): { w: number; h: number } {
  const dpr = 2;
  const fontSize = isActive ? 20 : 16;
  const padX = 14;
  const padY = 8;
  ctx.font = `800 ${fontSize}px system-ui, Tahoma, sans-serif`;
  const textW = ctx.measureText(text).width;
  const pillW = textW + padX * 2;
  const pillH = fontSize + padY * 2;
  const dotR = isActive ? 7.5 : 6;
  const gap = 7;
  const w = Math.ceil(Math.max(pillW, dotR * 2 + 6));
  const h = Math.ceil(pillH + gap + dotR * 2 + 4);

  canvas.width = w * dpr;
  canvas.height = h * dpr;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, w, h);

  const pillX = (w - pillW) / 2;
  ctx.fillStyle = isActive ? 'rgba(28, 59, 155, 0.98)' : 'rgba(15, 23, 42, 0.94)';
  roundRect(ctx, pillX, 0, pillW, pillH, isActive ? 10 : 8);
  ctx.fill();
  ctx.strokeStyle = isActive ? 'rgba(186, 230, 253, 0.85)' : 'rgba(255, 255, 255, 0.5)';
  ctx.lineWidth = isActive ? 2.25 : 1.75;
  ctx.stroke();

  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, w / 2, pillH / 2);

  const dotCy = pillH + gap + dotR;
  ctx.beginPath();
  ctx.arc(w / 2, dotCy, dotR, 0, Math.PI * 2);
  ctx.fillStyle = isActive ? '#bae6fd' : '#ffffff';
  ctx.fill();
  ctx.strokeStyle = 'rgba(15, 23, 42, 0.92)';
  ctx.lineWidth = isActive ? 2.75 : 2.25;
  ctx.stroke();

  return { w, h };
}

function syncCachedSprite(entry: SpriteEntry, text: string, isActive: boolean) {
  if (entry.lastActive === isActive && entry.lastText === text) return;

  const ctx = entry.canvas.getContext('2d');
  if (ctx) {
    const { w, h } = paintLabel(ctx, entry.canvas, text, isActive);
    entry.texture.needsUpdate = true;
    const k = isActive ? LABEL_SCREEN_SCALE.active : LABEL_SCREEN_SCALE.default;
    entry.sprite.scale.set(w * k, h * k, 1);
  }
  entry.lastActive = isActive;
  entry.lastText = text;
}

function getCachedSpriteEntry(cityId: string): SpriteEntry {
  let entry = cache.get(cityId);
  if (entry) return entry;

  const canvas = document.createElement('canvas');
  const texture = new CanvasTexture(canvas);
  texture.minFilter = LinearFilter;
  texture.magFilter = LinearFilter;
    const material = new SpriteMaterial({
      map: texture,
      transparent: true,
      depthTest: true,
      depthWrite: false,
      sizeAttenuation: false,
    });
  const sprite = new Sprite(material);
  sprite.center.set(0.5, 0);
  sprite.renderOrder = 10;
  entry = {
    sprite,
    canvas,
    texture,
    lastActive: false,
    lastText: '',
  };
  cache.set(cityId, entry);
  return entry;
}

/**
 * Fresh sprite per globe object — three-globe requires unique Object3D instances.
 * Anchor: bottom-center (dot) on lat/lng at objectAltitude.
 */
export function getCityLabelSprite(cityId: string, isActive: boolean) {
  const text = getGlobeCityLabel(cityId);
  const entry = getCachedSpriteEntry(cityId);
  syncCachedSprite(entry, text, isActive);

  const clone = entry.sprite.clone();
  clone.center.copy(entry.sprite.center);
  clone.scale.copy(entry.sprite.scale);
  clone.renderOrder = entry.sprite.renderOrder;
  return clone;
}

export function disposeCityLabelSprites() {
  cache.forEach((entry) => {
    entry.texture.dispose();
    entry.sprite.material.dispose();
  });
  cache.clear();
}
